import React, { useCallback, useEffect, useState } from "react";
import { adminApi } from "@/admin/AdminAuthContext";
import { Loader2, Inbox as InboxIcon, Eye, Globe, MapPin, Sparkles, ThumbsUp, ThumbsDown, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import ProspectDetailPanel from "@/admin/ProspectDetailPanel";

function scoreClasses(score) {
  if (score == null) return "text-white/40";
  if (score >= 70) return "text-emerald-300";
  if (score >= 40) return "text-amber-300";
  return "text-[#FFB4BB]";
}

export default function AdminInbox() {
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState({ pending_validation: 0, raw: 0, ready_to_send: 0 });
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);
  const [generatingBatch, setGeneratingBatch] = useState(false);
  const [updatingId, setUpdatingId] = useState(null);

  const triggerReload = useCallback(() => setReloadKey((k) => k + 1), []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const { data } = await adminApi.get("/admin/prospects?status=pending_validation&limit=200");
        if (cancelled) return;
        setItems(data.items);
        setStats(data.stats);
      } catch (e) {
        if (!cancelled) toast.error("Impossible de charger les prospects à valider.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [reloadKey]);

  const onApprove = async (id, e) => {
    e?.stopPropagation();
    setUpdatingId(id);
    try {
      await adminApi.patch(`/admin/prospects/${id}/status`, { status: "ready_to_send" });
      toast.success("Prospect approuvé.");
      triggerReload();
    } catch {
      toast.error("Approbation échouée.");
    } finally {
      setUpdatingId(null);
    }
  };

  const onReject = async (id, e) => {
    e?.stopPropagation();
    setUpdatingId(id);
    try {
      await adminApi.patch(`/admin/prospects/${id}/status`, { status: "rejected" });
      toast.success("Prospect rejeté.");
      triggerReload();
    } catch {
      toast.error("Rejet échoué.");
    } finally {
      setUpdatingId(null);
    }
  };

  const onGenerateBatch = async () => {
    if (!window.confirm("Générer audit + email pour les prospects scorés sans brouillon (max 10) ?")) return;
    setGeneratingBatch(true);
    try {
      const { data } = await adminApi.post("/admin/prospects/generate-batch");
      if (data.generated === 0 && data.failed === 0) {
        toast.info(data.message || "Aucun prospect à générer.");
      } else {
        toast.success(`${data.generated} brouillon(s) généré(s)${data.failed ? `, ${data.failed} échec(s)` : ""}.`);
      }
      triggerReload();
    } catch {
      toast.error("Génération batch échouée.");
    } finally {
      setGeneratingBatch(false);
    }
  };

  return (
    <div className="px-6 md:px-10 py-10 max-w-7xl" data-testid="admin-inbox">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-2">
        <div>
          <div className="text-xs tracking-[2px] text-white/40 font-semibold">— BOÎTE DE VALIDATION</div>
          <h1 className="mt-2 font-outfit text-3xl md:text-4xl font-light tracking-tight inline-flex items-center gap-3">
            <InboxIcon size={26} className="text-white/40" /> À valider
            <span className="text-base text-white/45 font-light">({items.length})</span>
          </h1>
        </div>
        <button
          type="button"
          onClick={onGenerateBatch}
          disabled={generatingBatch}
          data-testid="inbox-generate-batch"
          className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full text-xs uppercase tracking-[0.12em] font-semibold gradient-bg text-white hover:shadow-[0_0_30px_rgba(229,36,49,0.4)] transition disabled:opacity-60"
          title="Lancer la génération IA pour les prospects scorés sans brouillon"
        >
          {generatingBatch ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles size={14} />}
          {generatingBatch ? "Génération…" : "Générer IA (10)"}
        </button>
      </div>
      <p className="text-white/55 text-sm mb-6">
        Chaque prospect ci-dessous a un audit + un email générés par l&apos;IA. Relisez, ajustez, puis approuvez ou rejetez.
      </p>

      {loading ? (
        <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-10 flex items-center justify-center gap-2 text-white/40 text-sm">
          <Loader2 className="w-4 h-4 animate-spin" /> Chargement…
        </div>
      ) : items.length === 0 ? (
        <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-12 text-center" data-testid="inbox-empty">
          <InboxIcon size={32} className="mx-auto text-white/20" />
          <div className="mt-3 text-sm text-white/65">Aucun prospect en attente de validation.</div>
          <div className="mt-1 text-xs text-white/40">
            Lance la génération IA depuis cette page ou depuis « Tous les prospects ».
          </div>
        </div>
      ) : (
        <div className="space-y-3" data-testid="inbox-list">
          {items.map((p) => (
            <div
              key={p.id}
              role="button"
              tabIndex={0}
              onClick={() => setSelectedId(p.id)}
              onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setSelectedId(p.id); } }}
              data-testid={`inbox-row-${p.id}`}
              className="w-full text-left rounded-2xl border border-white/10 bg-white/[0.02] hover:bg-white/[0.04] hover:border-white/20 transition p-5 group cursor-pointer focus:outline-none focus:border-[#E52431]/40"
            >
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-3 flex-wrap">
                    <h3 className="font-medium text-white truncate">{p.nom_entreprise}</h3>
                    <span className={`font-mono font-semibold text-sm ${scoreClasses(p.score)}`}>
                      {p.score ?? "—"}/100
                    </span>
                  </div>
                  <div className="mt-1.5 flex items-center gap-4 text-xs text-white/55 flex-wrap">
                    {p.ville && <span className="inline-flex items-center gap-1"><MapPin size={11} /> {p.ville}</span>}
                    {p.site_web && (
                      <span className="inline-flex items-center gap-1 truncate max-w-[260px]">
                        <Globe size={11} /> {p.site_web.replace(/^https?:\/\//, "")}
                      </span>
                    )}
                    {p.secteur && <span className="text-white/40">{p.secteur}</span>}
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <button
                    onClick={(e) => onReject(p.id, e)}
                    disabled={updatingId === p.id}
                    data-testid={`inbox-reject-${p.id}`}
                    className="inline-flex items-center gap-1 px-3 py-2 rounded-full text-xs uppercase tracking-[0.1em] font-semibold bg-white/5 border border-white/10 text-white/65 hover:bg-white/10 hover:text-white transition disabled:opacity-50"
                    title="Rejeter"
                  >
                    <ThumbsDown size={12} /> Rejeter
                  </button>
                  <button
                    onClick={(e) => onApprove(p.id, e)}
                    disabled={updatingId === p.id}
                    data-testid={`inbox-approve-${p.id}`}
                    className="inline-flex items-center gap-1 px-3 py-2 rounded-full text-xs uppercase tracking-[0.1em] font-semibold bg-emerald-500/15 border border-emerald-500/30 text-emerald-200 hover:bg-emerald-500/25 transition disabled:opacity-50"
                    title="Approuver"
                  >
                    <ThumbsUp size={12} /> Approuver
                  </button>
                  <span
                    className="inline-flex items-center gap-1 px-3 py-2 rounded-full text-[10px] uppercase tracking-[0.1em] font-semibold bg-white/5 border border-white/10 text-white/55 group-hover:text-white transition"
                  >
                    <Eye size={11} /> Réviser
                  </span>
                </div>
              </div>
              {/* Preview of email subject if available */}
              {p.email_subject && (
                <div className="mt-3 pt-3 border-t border-white/5 text-xs text-white/70 truncate flex items-center gap-2">
                  <Sparkles size={11} className="text-[#E52431] shrink-0" />
                  <span className="font-medium">{p.email_subject}</span>
                </div>
              )}
              {!p.email_subject && !p.has_draft && (
                <div className="mt-3 pt-3 border-t border-white/5 text-xs text-amber-300/80 flex items-center gap-2">
                  <AlertCircle size={11} /> Brouillon manquant — ouvrez le détail pour générer.
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      <ProspectDetailPanel
        prospectId={selectedId}
        onClose={() => setSelectedId(null)}
        onRescored={triggerReload}
      />
    </div>
  );
}
